<template>
  <Teleport to="body">
    <div ref="modal" class="modal fade" tabindex="-1" aria-hidden="true">
      <div
        class="
          modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable
        "
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Leaves - {{ member?.name || memberId }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              v-on:click="$emit('update:show', false)"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary"></div>
            </div>
            <div v-else>
              <div
                class="d-flex justify-content-between align-items-center mb-3"
              >
                <button
                  class="btn btn-outline-secondary btn-sm"
                  v-on:click="prevMonth"
                >
                  &laquo;
                </button>
                <div class="fw-bold">{{ monthLabel }}</div>
                <button
                  class="btn btn-outline-secondary btn-sm"
                  v-on:click="nextMonth"
                >
                  &raquo;
                </button>
              </div>

              <div class="row g-1 text-center mb-2">
                <div
                  v-for="wd in weekdayHeaders"
                  :key="wd"
                  class="col text-muted small fw-bold"
                >
                  {{ wd }}
                </div>
              </div>

              <div v-for="(week, wi) in weeks" :key="wi" class="row g-1 mb-1">
                <div v-for="(cell, ci) in week" :key="ci" class="col">
                  <div
                    v-if="cell"
                    class="leave-day-cell"
                    :class="{
                      'leave-day-muted': !cell.inMonth,
                      'leave-day-today': cell.isToday,
                      'leave-day-marked': cell.reasonId,
                    }"
                    :style="
                      cell.reasonId
                        ? { background: reasonColor(cell.reasonId) }
                        : {}
                    "
                    v-on:click="cell.inMonth && toggleDay(cell.dateKey)"
                    :title="cell.leaveTitle"
                  >
                    {{ cell.day }}
                  </div>
                  <div v-else></div>
                </div>
              </div>

              <div class="d-flex flex-wrap gap-2 mt-3">
                <div class="fw-bold me-1 align-self-center">Reason:</div>
                <button
                  v-for="reason in reasons"
                  :key="reason.id"
                  type="button"
                  class="btn btn-sm"
                  :class="
                    activeReason === reason.id
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                  "
                  v-on:click="activeReason = reason.id"
                >
                  <span
                    class="d-inline-block rounded-circle me-1"
                    style="width: 10px; height: 10px"
                    :style="{ background: reason.color }"
                  ></span>
                  {{ reason.name }}
                </button>
              </div>

              <div v-if="monthLeaves.length > 0" class="mt-3">
                <div class="fw-bold mb-2">Days in {{ monthLabel }}:</div>
                <div
                  v-for="entry in monthLeaves"
                  :key="entry.day"
                  class="
                    d-flex
                    align-items-center
                    gap-2
                    mb-2
                    p-2
                    border
                    rounded
                  "
                  :style="{
                    background: (reasonColor(entry.reasonId) || '') + '22',
                  }"
                >
                  <div class="fw-bold" style="width: 90px">{{ entry.day }}</div>
                  <select
                    class="form-select form-select-sm"
                    v-model="entry.reasonId"
                    v-on:change="setReason(entry.day, entry.reasonId)"
                  >
                    <option
                      v-for="reason in reasons"
                      :key="reason.id"
                      :value="reason.id"
                    >
                      {{ reason.name }}
                    </option>
                  </select>
                  <button
                    class="btn btn-sm btn-outline-danger ms-auto"
                    v-on:click="removeDay(entry.day)"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              class="btn btn-primary"
              :class="{ 'loading-spin': saving }"
              :disabled="saving || loading"
              v-on:click="save"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import http from "../../modules/http";

const bootstrap = require("bootstrap");

const WEEKDAY_HEADERS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function dateKey(year, month, day) {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

export default {
  name: "LeaveModal",
  props: {
    show: Boolean,
    memberId: Number,
    memberName: String,
    memberEmail: String,
    type: {
      type: String,
      default: "teams",
    },
  },
  emits: ["update:show", "saved"],
  data() {
    const now = new Date();
    return {
      myModal: null,
      loading: false,
      saving: false,
      reasons: [],
      member: null,
      original: {},
      edited: {},
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth(),
      activeReason: null,
      weekdayHeaders: WEEKDAY_HEADERS,
    };
  },
  computed: {
    monthLabel() {
      const names = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
      return `${names[this.viewMonth]} ${this.viewYear}`;
    },
    weeks() {
      const first = new Date(this.viewYear, this.viewMonth, 1);
      const daysInMonth = new Date(
        this.viewYear,
        this.viewMonth + 1,
        0
      ).getDate();
      const startOffset = (first.getDay() + 6) % 7;
      const today = new Date();
      const todayKey = dateKey(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      const cells = [];
      for (let i = 0; i < startOffset; i++) {
        cells.push(null);
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const key = dateKey(this.viewYear, this.viewMonth, d);
        const reasonId = this.displayedReason(key);
        const reason = this.reasonById(reasonId);
        cells.push({
          day: d,
          dateKey: key,
          inMonth: true,
          isToday: key === todayKey,
          reasonId,
          leaveTitle: reason ? reason.name : "",
        });
      }
      while (cells.length % 7 !== 0) {
        cells.push(null);
      }

      const weeks = [];
      for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7));
      }
      return weeks;
    },
    monthLeaves() {
      const entries = [];
      const daysInMonth = new Date(
        this.viewYear,
        this.viewMonth + 1,
        0
      ).getDate();
      for (let d = 1; d <= daysInMonth; d++) {
        const key = dateKey(this.viewYear, this.viewMonth, d);
        const reasonId = this.displayedReason(key);
        if (reasonId) {
          entries.push({ day: key, reasonId });
        }
      }
      return entries;
    },
  },
  mounted() {
    this.myModal = new bootstrap.Modal(this.$refs.modal, {
      backdrop: "static",
    });
    if (this.show) {
      this.load();
      this.myModal.show();
    } else {
      this.myModal.hide();
    }
    const _this = this;
    this.$refs.modal.addEventListener("hidden.bs.modal", function () {
      _this.$emit("update:show", false);
    });
  },
  watch: {
    show() {
      if (this.show) {
        this.load();
        this.myModal.show();
      } else {
        this.myModal.hide();
      }
    },
  },
  methods: {
    load() {
      this.loading = true;
      this.original = {};
      this.edited = {};
      http
        .fetch(`/crm/${this.type}/leaves?crm_item_id=${this.memberId}`)
        .then((data) => {
          this.member = data.member;
          this.reasons = data.reasons || [];
          if (this.reasons.length > 0 && this.activeReason === null) {
            this.activeReason = this.reasons[0].id;
          }
          for (const leave of data.leaves || []) {
            this.original[leave.day] = leave.reason_category_id;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    displayedReason(key) {
      if (Object.prototype.hasOwnProperty.call(this.edited, key)) {
        return this.edited[key];
      }
      return this.original[key] ?? null;
    },
    reasonById(id) {
      if (!id) return null;
      return this.reasons.find((r) => r.id === id);
    },
    reasonColor(id) {
      return this.reasonById(id)?.color || null;
    },
    toggleDay(key) {
      const current = this.displayedReason(key);
      if (current) {
        this.edited[key] = null;
      } else {
        if (this.activeReason === null || this.activeReason === undefined) {
          if (this.reasons.length === 0) return;
          this.activeReason = this.reasons[0].id;
        }
        this.edited = { ...this.edited, [key]: this.activeReason };
      }
    },
    setReason(key, reasonId) {
      if (reasonId === null || reasonId === "") {
        this.edited[key] = null;
      } else {
        this.edited[key] = reasonId;
      }
    },
    removeDay(key) {
      this.edited[key] = null;
    },
    prevMonth() {
      if (this.viewMonth === 0) {
        this.viewMonth = 11;
        this.viewYear--;
      } else {
        this.viewMonth--;
      }
    },
    nextMonth() {
      if (this.viewMonth === 11) {
        this.viewMonth = 0;
        this.viewYear++;
      } else {
        this.viewMonth++;
      }
    },
    save() {
      const payload = [];
      const keys = new Set([
        ...Object.keys(this.original),
        ...Object.keys(this.edited),
      ]);
      for (const key of keys) {
        if (!Object.prototype.hasOwnProperty.call(this.edited, key)) {
          continue;
        }
        const originalReason = this.original[key] ?? null;
        const newReason = this.edited[key] ?? null;
        if (newReason !== originalReason) {
          payload.push({
            crm_item_id: this.memberId,
            day: key,
            reason_category_id: newReason,
          });
        }
      }
      if (payload.length === 0) {
        this.myModal.hide();
        this.$emit("saved", []);
        return;
      }
      this.saving = true;
      http
        .fetch(`/crm/${this.type}/leaves`, { leaves: payload }, true)
        .then((data) => {
          this.saving = false;
          this.$emit("saved", data);
          this.myModal.hide();
        })
        .catch(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
.leave-day-cell {
  min-height: 38px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  color: #212529;
}
.leave-day-cell:hover {
  border-color: #6c757d;
}
.leave-day-muted {
  opacity: 0.4;
}
.leave-day-today {
  box-shadow: inset 0 0 0 2px #0d6efd;
}
.leave-day-marked {
  color: #fff;
  font-weight: bold;
}
</style>
